import { useRef, useState } from 'react';
import { FiSearch, FiShield } from 'react-icons/fi';
import {
  getStudentAccommodation,
  setStudentAccommodation,
  StudentAccommodationState
} from '../../api/admin';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { IepTimerMode } from '../../types/accommodations';

const TIMER_OPTIONS: Array<{ value: IepTimerMode; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: '1.5x', label: '1.5× time' },
  { value: '2x', label: '2× time' },
  { value: 'untimed', label: 'Untimed' }
];

export function StudentAccommodationsCard() {
  const [email, setEmail] = useState('');
  const [student, setStudent] = useState<StudentAccommodationState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const load = async () => {
    const requestedEmail = email.trim();
    if (!requestedEmail || isLoading) return;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setMessage(null);
    try {
      const loadedStudent = await getStudentAccommodation(requestedEmail);
      if (requestId === requestIdRef.current) setStudent(loadedStudent);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setStudent(null);
        setMessage(error instanceof Error ? error.message : 'Student not found.');
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  const update = async (enabled: boolean, timerMode: IepTimerMode) => {
    if (!student) return;
    const requestId = ++requestIdRef.current;
    const studentEmail = student.email;
    setIsLoading(true);
    setMessage(null);
    try {
      const updated = await setStudentAccommodation(studentEmail, enabled, timerMode);
      if (requestId === requestIdRef.current) {
        setStudent(updated);
        setMessage('IEP accommodations updated.');
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setMessage(error instanceof Error ? error.message : 'Failed to update accommodations.');
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  return (
    <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100">
      <div className="bg-gradient-to-r from-teal-600 to-blue-600 py-4 px-6">
        <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
          <FiShield className="text-cyan-100" /> Student IEP accommodations
        </h2>
      </div>
      <CardContent className="p-6 space-y-4">
        <p className="text-sm text-gray-600">
          Configure the timer, require two successful demonstrations per fact, preserve earned demonstrations after mistakes, and award active-time XP below the normal accuracy floor. Standard multi-day progression still applies.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            value={email}
            onChange={event => {
              requestIdRef.current += 1;
              setEmail(event.target.value);
              setStudent(null);
              setMessage(null);
              setIsLoading(false);
            }}
            onKeyDown={event => { if (event.key === 'Enter' && !isLoading) load(); }}
            placeholder="student@example.com"
          />
          <Button onClick={load} disabled={isLoading || !email.trim()} className="flex gap-2">
            <FiSearch /> {isLoading ? 'Loading…' : 'Find student'}
          </Button>
        </div>

        {student && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-gray-50 rounded-lg border">
            <div>
              <div className="font-medium text-gray-900">{student.email}</div>
              <div className="text-sm text-gray-600">
                Preset is {student.iepAccommodationsEnabled ? 'enabled' : 'disabled'}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                Timer
                <select
                  value={student.iepTimerMode}
                  disabled={isLoading || !student.iepAccommodationsEnabled}
                  onChange={event => update(student.iepAccommodationsEnabled, event.target.value as IepTimerMode)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  aria-label="IEP timer mode"
                >
                  {TIMER_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={student.iepAccommodationsEnabled}
                  disabled={isLoading}
                  onChange={event => update(event.target.checked, student.iepTimerMode)}
                  className="h-5 w-5 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm font-medium">IEP preset</span>
              </label>
            </div>
            {!student.iepAccommodationsEnabled && (
              <div className="text-xs text-gray-500 sm:basis-full">
                Enable the IEP preset to configure the timer.
              </div>
            )}
          </div>
        )}

        {message && <div className="text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded p-3">{message}</div>}
      </CardContent>
    </Card>
  );
}
